/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  initialChartEditPage,
  createEmptyChartDraft,
  createDraftFromVersion,
  publishChartDraft,
  publishChartVersion,
} from '../../../app/page/chart/chart.actions';
import { openDialog } from '../../../app/dialog/dialog.actions';
import { PageLayout } from '../common/PageLayout';
import { chartPageSelectors } from '../../../app/page/page-storage/chart/chart.selectors';
import { chartSelectors } from '../../../app/resource/chart/chart.selectors';
import { authSelectors } from '../../../app/auth/auth.selectors';
import { userSelectors } from '../../../app/resource/user/user.selectors';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { convertAndFormatDateToUserTimeZone } from '../../../utils/date.utils';
import { DefaultChart } from '../ChartPage/DefaultChart';
import { DEFAULT_CHART_TYPE } from '../../../app/page/chart/chart.constants';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { ChartVersionSidebar } from './ChartVersionSidebar';
import { useApiRequestState } from '../../../utils/api.utils';
import { ChartInstructions } from '../ChartPage/ChartInstructions';
import { Breadcrumb } from '../common/Breadcrumb';
import { DEFAULT_CHART_CATEGORY_TITLE, DEFAULT_CHART_ITEM_TITLE } from '../AgencyToolsPage/DefaultChartCategory';

// Grow to fill viewport height; minHeight 0 allows nested flex children to shrink and scroll.
const fillHeight: React.CSSProperties = { flex: 1, minHeight: 0 };
// Vertical flex stack for panel → panel-body → chart row.
const fillHeightColumn: React.CSSProperties = { ...fillHeight, display: 'flex', flexDirection: 'column' };

export const ChartEditPage: FunctionComponent = () => {
  const dispatch = useDispatch();

  const nodeIds = useSelect(chartPageSelectors.selectNodes);
  const versions = useSelect(chartPageSelectors.selectChartVersions);
  const selectedChartId = useSelect(chartPageSelectors.selectSelectedChartId);
  const isDraftSelected = useSelect(chartPageSelectors.selectIsDraftSelected);
  const draftChart = useSelect(chartSelectors.selectDraftChart);
  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const loggedUserTimezone = loggedUser?.timezone ?? null;
  const showSpinner = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'chartEdit');
  const [isPublishingVersion] = useApiRequestState('publishChartVersion');
  const [isCreatingEmptyDraft] = useApiRequestState('createEmptyDraft');
  const [isCreatingDraftFromVersion] = useApiRequestState('createDraftFromVersion');

  useEffect(() => {
    dispatch(initialChartEditPage());
  }, [dispatch]);

  const selectedVersion = useMemo(() => versions.find((version) => String(version.id) === String(selectedChartId)), [
    versions,
    selectedChartId,
  ]);
  const canPublishDraft = Boolean(isDraftSelected && draftChart);
  const canPublishVersion = Boolean(!isDraftSelected && selectedVersion && !selectedVersion.published);
  const canPublish = canPublishDraft || canPublishVersion;
  const publishLabel = isDraftSelected ? 'Publish' : 'Re-publish';
  const publishTitle = isDraftSelected ? 'Publish draft as a new version' : 'Re-publish this version';
  const canCreateDraftFromVersion = Boolean(!isDraftSelected && selectedVersion);

  const onPublish = useCallback(() => {
    if (isDraftSelected) {
      dispatch(
        openDialog('confirmation', {
          title: 'Publish draft',
          message: 'Publish this draft as a new version?',
          confirmButton: 'Publish',
          onConfirm: () => dispatch(publishChartDraft()),
        }),
      );
      return;
    }
    if (!canPublishVersion || isPublishingVersion) {
      return;
    }
    dispatch(publishChartVersion());
  }, [canPublishVersion, dispatch, isDraftSelected, isPublishingVersion]);

  const onConfirmNewEmptyDraft = useCallback(() => {
    dispatch(createEmptyChartDraft());
  }, [dispatch]);

  const onNewEmptyDraft = useCallback(() => {
    if (isCreatingEmptyDraft) {
      return;
    }
    dispatch(
      openDialog('confirmation', {
        title: 'New empty draft',
        message: draftChart
          ? 'The current draft will be replaced with an empty draft. This cannot be undone. Do you want to continue?'
          : 'Create a new empty draft with no seats. Do you want to continue?',
        confirmButton: 'Create draft',
        onConfirm: onConfirmNewEmptyDraft,
      }),
    );
  }, [dispatch, draftChart, isCreatingEmptyDraft, onConfirmNewEmptyDraft]);

  const onConfirmCreateDraftFromVersion = useCallback(() => {
    dispatch(createDraftFromVersion());
  }, [dispatch]);

  const onCreateDraftFromVersion = useCallback(() => {
    if (isCreatingDraftFromVersion || !selectedVersion) {
      return;
    }
    const versionDate = selectedVersion.lastPublishedAt ?? selectedVersion.updatedAt;
    const versionLabel =
      versionDate && loggedUserTimezone
        ? convertAndFormatDateToUserTimeZone(versionDate, loggedUserTimezone, 'MM/DD/YYYY h:mm A')
        : `Version ${selectedVersion.id}`;
    dispatch(
      openDialog('confirmation', {
        title: 'Create draft from version',
        message: draftChart
          ? `The current draft will be replaced with a copy of version ${versionLabel}. This cannot be undone. Do you want to continue?`
          : `Create a draft from version ${versionLabel}. Do you want to continue?`,
        confirmButton: 'Create draft',
        onConfirm: onConfirmCreateDraftFromVersion,
      }),
    );
  }, [
    dispatch,
    draftChart,
    isCreatingDraftFromVersion,
    loggedUserTimezone,
    onConfirmCreateDraftFromVersion,
    selectedVersion,
  ]);

  const openNodeFormDialog = useCallback(() => {
    dispatch(openDialog('nodeFormDialog', { chartType: DEFAULT_CHART_TYPE }));
  }, [dispatch]);

  const hasNodes = nodeIds.length > 0;
  const showDraftEmptyCta = isDraftSelected && !hasNodes && !showSpinner;
  const showVersionEmptyMessage = !isDraftSelected && !hasNodes && !showSpinner;
  const showDraftChart = isDraftSelected && hasNodes && !showSpinner;
  const showVersionChart = !isDraftSelected && hasNodes && !showSpinner;

  return (
    <PageLayout showLoader={showSpinner}>
      <div className="row-vertical" style={fillHeight}>
        <div className="col-12" style={fillHeightColumn}>
          <div className="panel" style={{ ...fillHeightColumn, overflow: 'hidden' }}>
            <div className="no-border panel-header small-header breadcrumb-header">
              <div className="breadcrumb-header__content">
                <Breadcrumb
                  items={[
                    { name: 'Agency Tools', path: '/agency-tools' },
                    { name: DEFAULT_CHART_CATEGORY_TITLE, path: '/agency-tools' },
                    { name: DEFAULT_CHART_ITEM_TITLE },
                  ]}
                />
                <div className="breadcrumb-header__title-row">
                  <div className="flex aic">
                    <h2 className="bold mr-2">Main Chart</h2>
                    <ChartInstructions isEditPage />
                  </div>
                  <div className="flex aic jcr">
                    {canCreateDraftFromVersion && (
                      <a
                        className="btn btn-md"
                        title="Replace the current draft with a copy of the selected version"
                        onClick={onCreateDraftFromVersion}
                        style={isCreatingDraftFromVersion ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                      >
                        Create draft from this version
                      </a>
                    )}
                    <a
                      className="btn btn-md"
                      title="Replace the current draft with an empty draft"
                      onClick={onNewEmptyDraft}
                      style={isCreatingEmptyDraft ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                    >
                      New empty draft
                    </a>
                    {canPublish && (
                      <a
                        className="btn btn-md"
                        title={publishTitle}
                        onClick={onPublish}
                        style={isPublishingVersion ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                      >
                        {publishLabel}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="panel-body pt-0" style={fillHeightColumn}>
              <div className="flex" style={{ ...fillHeight, alignItems: 'stretch' }}>
                <ChartVersionSidebar
                  versions={versions}
                  selectedChartId={selectedChartId}
                  draftChart={draftChart}
                  hasDraft={Boolean(draftChart)}
                  isDraftSelected={isDraftSelected}
                />
                <div style={{ ...fillHeight, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
                  {showDraftEmptyCta && (
                    <div className="flex flex-column align-center p-3">
                      <h2 className="mb-3">Start building your organization chart.</h2>
                      <a
                        className="btn btn-md popup_button"
                        title="Create First Node"
                        onClick={openNodeFormDialog}
                        style={{ width: 170, margin: 'auto' }}
                      >
                        <i className="icon-plus mr-1"></i> Create your first seat
                      </a>
                    </div>
                  )}
                  {showVersionEmptyMessage && (
                    <div className="flex flex-column align-center p-3">
                      <p className="mb-0">No chart data for the selected version.</p>
                    </div>
                  )}
                  {showDraftChart && (
                    <>
                      <div
                        className="dropdown plus_button_container"
                        style={{ position: 'absolute', top: 16, right: 16, zIndex: 1111 }}
                      >
                        <span
                          className="plus_button themed-bg popupp"
                          title="Create root seat"
                          onClick={openNodeFormDialog}
                          style={{ position: 'relative', bottom: 'auto', right: 'auto' }}
                        >
                          <i className="icon-plus"></i>
                        </span>
                      </div>
                      <DefaultChart nodeIds={nodeIds} chartType={DEFAULT_CHART_TYPE} readOnly={false} />
                    </>
                  )}
                  {showVersionChart && <DefaultChart nodeIds={nodeIds} chartType={DEFAULT_CHART_TYPE} readOnly />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
